import { analyticsStatusDataRefType } from '../../../Utils/types';
import { doesFiTypeExist } from './assetUtils';
import { AssetCategory } from './constants';

export const getMutualFundTotalValue = (
  data: any,
  datafromAnalyticsStatus: React.RefObject<analyticsStatusDataRefType | null>,
  mutualFundsRawData: any[],
  mfLinkedAccounts?: any[]
): number => {
  // Analytics value
  const analyticsValue =
    data?.current?.MUTUAL_FUNDS?.data?.overallAnalysis?.totalPortfolioValue ||
    datafromAnalyticsStatus?.current?.mutualFunds;
  if (analyticsValue) return Math.round(analyticsValue);

  // Closing balance sum from linked accounts
  if (mfLinkedAccounts && mfLinkedAccounts.length > 0) {
    const closingBalanceSum = mfLinkedAccounts.reduce(
      (total: number, acc: any) => total + (Number(acc.closingBalance) || 0),
      0
    );
    if (closingBalanceSum > 0) return Math.round(closingBalanceSum);
  }

  // Raw data fallback
  if (mutualFundsRawData && mutualFundsRawData.length > 0) {
    let total = 0;
    mutualFundsRawData.forEach((mfAccount: any) => {
      const currentValue = parseFloat(
        mfAccount?.rawData?.Summary?.currentValue || '0'
      );
      if (currentValue > 0) {
        total += currentValue;
      } else {
        const holdingsList =
          mfAccount?.rawData?.Summary?.Investment?.Holdings?.Holding || [];
        holdingsList.forEach((holding: any) => {
          total +=
            parseFloat(holding.closingUnits || '0') *
            parseFloat(holding.nav || '0');
        });
      }
    });
    if (total > 0) return Math.round(total);
  }

  return 0;
};

export const getMutualFundTotalValueRaw = (
  mutualFundsRawData: any[],
  statusData: any
): number => {
  let total = 0;
  if (!doesFiTypeExist(AssetCategory.MUTUAL_FUNDS, statusData)) {
    return total;
  }
  mutualFundsRawData?.forEach((mfAccount: any) => {
    const currentValue = parseFloat(
      mfAccount?.rawData?.Summary?.currentValue || '0'
    );
    if (currentValue > 0) {
      total += currentValue;
    } else {
      const holdingsList =
        mfAccount?.rawData?.Summary?.Investment?.Holdings?.Holding || [];
      holdingsList.forEach((holding: any) => {
        total +=
          parseFloat(holding.closingUnits || '0') *
          parseFloat(holding.nav || '0');
      });
    }
  });
  return Math.round(total);
};

export const listMutualfundHoldings = (mutualFundsRawData: any) => {
  let holdings: any[] = [];
  
  if (mutualFundsRawData?.length > 0) {
    const map = new Map<string, any>();
    mutualFundsRawData.forEach((mfAccount: any) => { 
      const listOfHoldings =
        mfAccount?.rawData?.Summary?.Investment?.Holdings?.Holding || [];
      listOfHoldings.forEach((item: any) => {        
        const key = `${item.isin}_${item.folioNo}`;
        if (map.has(key)) return;
        const units = parseFloat(item.closingUnits || 0);
        const nav = parseFloat(item.nav || 0);
        map.set(key, {
          id: key,
          isin: item.isin,
          mutualFundName: item.isinDescription || item.amc || '',
          fundHouse: item.amc || '',
          schemeCategory: item.schemeTypes || item.schemeCategory || '',
          units,
          nav,
          // totalPortfolioValue: units * nav,
          currentValue: units * nav,
          accountDetails: [{ folioNo: item.folioNo || '' }],
          folioNumbers: [item.folioNo || ''],
          folioCount: 1,
        });
      });
    });
    holdings = Array.from(map.values());
  }
  holdings.sort(
    (a: any, b: any) => (b.currentValue || 0) - (a.currentValue || 0)
  );
  return holdings;
};

export const getSchemeType = (category: string) => {
  const normalizedCategory = category.toLowerCase();
  if (normalizedCategory.includes('equity')) return 'Equity';
  if (normalizedCategory.includes('debt')) return 'Debt';
  if (normalizedCategory.includes('hybrid')) return 'Hybrid';
  return 'Other';
};

export const getMfPortfolioCategoryDistribution = (
  mutualFundList: any[],
  balance: number
) => {
  const distributionMap: Record<string, { count: number; value: number }> = {};
  mutualFundList.forEach((fund: any) => {
    const schemeType = getSchemeType(fund.schemeCategory);
    if (!distributionMap[schemeType]) {
      distributionMap[schemeType] = {
        count: 0,
        value: 0,
      };
    }

    distributionMap[schemeType].count++;
    distributionMap[schemeType].value += fund.currentValue || 0;
  });

  return Object.entries(distributionMap)
    .map(([type, { value }]) => ({
      type,
      pct: balance ? Math.round((value / balance) * 100) : 0,
    }))
    .sort((a, b) => b.pct - a.pct);
};
